import { useEffect, useCallback, useRef } from 'react';
import { usePlatform } from '~/services/platform';
import { kioskStore } from '~/stores/kiosk.store';

const LONG_PRESS_DURATION_MS = 800; // 800ms for easier trigger

export type TVKeyHandler = {
  onLeft?: () => void;
  onUp?: () => void;
  onRight?: () => void;
  onDown?: () => void;
  onEnter?: () => void;
  onReturn?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onStop?: () => void;
  onRed?: () => void;
  onGreen?: () => void;
  onYellow?: () => void;
  onBlue?: () => void;
  // INFO button on TV remotes (the "i" key on Samsung/LG remotes).
  // Tizen INFO = 457, WebOS INFO = 457.
  // Used to toggle the debug / media-info overlay without taking over a directional key.
  onInfo?: () => void;
  // Long press handlers (hold for 1.5 seconds)
  onLongPressUp?: () => void;
  onLongPressDown?: () => void;
};

export const useTVRemote = (handlers?: TVKeyHandler): void => {
  const platform = usePlatform();
  const { keys } = platform;

  // Long press tracking
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef<boolean>(false);
  const pressedKeyRef = useRef<number | null>(null);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const { keyCode } = event;

      // Prevent repeated keydown events from firing long press multiple times
      if (pressedKeyRef.current === keyCode) {
        return;
      }

      // Start long press detection for UP/DOWN
      if (keyCode === keys.UP || keyCode === keys.DOWN) {
        pressedKeyRef.current = keyCode;
        longPressTriggeredRef.current = false;
        clearLongPressTimer();

        const handler =
          keyCode === keys.UP
            ? handlers?.onLongPressUp
            : handlers?.onLongPressDown;
        if (handler) {
          longPressTimerRef.current = setTimeout(() => {
            longPressTriggeredRef.current = true;
            handler();
          }, LONG_PRESS_DURATION_MS);
        }
        return; // Don't trigger regular handler on keydown, wait for keyup
      }

      switch (keyCode) {
        case keys.LEFT:
          handlers?.onLeft?.();
          break;
        case keys.ENTER:
          handlers?.onEnter?.();
          break;
        case keys.RIGHT:
          handlers?.onRight?.();
          break;
        case keys.RETURN:
          // 2026-06-01 (v1.0.108) — Anti-exit em kiosk locked. Antes o
          // default era platform.exit() em RETURN sem handler — Renato
          // testou em TV Tizen Consumer v1.0.107 e Back saía do app
          // mesmo com kiosk ligado (em telas que não definem onReturn,
          // ex: NoContent/Splash). Agora kiosk locked SEMPRE engole o
          // RETURN, independente de handler/scene.
          if (kioskStore.isLocked) {
            console.log('[useTVRemote] 🚫 RETURN bloqueado — kiosk locked');
            break;
          }
          if (handlers?.onReturn) {
            handlers.onReturn();
          } else if (platform.canExit()) {
            platform.exit();
          }
          break;
        case keys.PLAY:
          if (keys.PLAY !== null) handlers?.onPlay?.();
          break;
        case keys.PAUSE:
          if (keys.PAUSE !== null) handlers?.onPause?.();
          break;
        case keys.STOP:
          if (keys.STOP !== null) handlers?.onStop?.();
          break;
        case keys.RED:
          if (keys.RED !== null) handlers?.onRed?.();
          break;
        case keys.GREEN:
          if (keys.GREEN !== null) handlers?.onGreen?.();
          break;
        case keys.YELLOW:
          if (keys.YELLOW !== null) handlers?.onYellow?.();
          break;
        case keys.BLUE:
          if (keys.BLUE !== null) handlers?.onBlue?.();
          break;
        case keys.INFO:
          // Samsung/LG INFO ("i") — dedicated debug overlay toggle on TV.
          // Avoids overloading directional keys with non-navigation behavior.
          if (keys.INFO !== null) handlers?.onInfo?.();
          break;
        default:
          // Only log in development
          if (import.meta.env.DEV) {
            console.log('[TVRemote] Unhandled key:', keyCode);
          }
          break;
      }
    },
    [handlers, keys, platform, clearLongPressTimer],
  );

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      const { keyCode } = event;

      // Handle UP/DOWN key release
      if (keyCode === keys.UP || keyCode === keys.DOWN) {
        clearLongPressTimer();

        // If long press wasn't triggered, fire the regular handler
        if (!longPressTriggeredRef.current) {
          if (keyCode === keys.UP) {
            handlers?.onUp?.();
          } else {
            handlers?.onDown?.();
          }
        }

        pressedKeyRef.current = null;
        longPressTriggeredRef.current = false;
      }
    },
    [handlers, keys, clearLongPressTimer],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      clearLongPressTimer();
    };
  }, [handleKeyDown, handleKeyUp, clearLongPressTimer]);
};

export default useTVRemote;
